/**
 * The tile icon for a presentation type (CT-5183).
 *
 * ## What used to be here
 *
 * A frozen registry of the 28 types — id, category and label key each — written
 * when the module ran on mocked services and kept afterwards out of habit. By
 * 2026-10-07 nothing imported `WIDGET_BUILDER_TYPES`, `WIDGET_TYPE_CATEGORIES`
 * or `widgetTypeById` except the barrel that re-exported them, and the
 * `widgetBuilder.types.*` keys they pointed at were referenced nowhere: the
 * picker gets its types, names, descriptions and grouping from
 * `widget/type/list` through `useWidgetTypes`.
 *
 * Leaving it in place cost more than the bytes. It was a second, staler answer
 * to "which types exist", and while the catalog was serving 20 of 28 it was
 * the one that looked authoritative — it lists all 28, with ids the backend
 * does not use. Deleted with the hand-written binding map it kept company
 * (CT-5579).
 *
 * ## Why an icon map survives
 *
 * The catalog publishes `icon` but nothing reads it — `useWidgetTypes`
 * overwrites it with this lookup — and the tile needs a component, not a
 * string. Until the catalog's own icon vocabulary is one `lucide-react` can be
 * driven from, the drawing is chosen here.
 */

import {
  AlertTriangle,
  AreaChart,
  BarChart3,
  BarChartBig,
  ChartPie,
  Coins,
  Columns3,
  Filter,
  GalleryHorizontalEnd,
  Gauge,
  Grid3x3,
  IdCard,
  LayoutList,
  LineChart,
  List,
  Map,
  PieChart,
  Rss,
  ScatterChart,
  Share2,
  Sparkle,
  Sparkles,
  Table2,
  TrendingUp,
  Wallet,
} from 'lucide-react';

/** Keyed by the backend's `rendererKey`, lower-case, as the catalog sends it. */
const ICON_BY_RENDERER = Object.freeze({
  kpi: TrendingUp,
  stat_row: LayoutList,
  gauge: Gauge,
  line_chart: LineChart,
  bar_chart: BarChart3,
  donut: ChartPie,
  funnel: Filter,
  heat_map: Grid3x3,
  correlation: ScatterChart,
  map: Map,
  pie: PieChart,
  area: AreaChart,
  stacked_bar: BarChartBig,
  sparkline: TrendingUp,
  ai_summary: Sparkles,
  next_best_action: Sparkle,
  table: Table2,
  list: List,
  profile_card: IdCard,
  connections: Share2,
  carousel: GalleryHorizontalEnd,
  board: Columns3,
  feed: Rss,
  alerts: AlertTriangle,
  cost_kpi: Coins,
  usage_heatmap: Grid3x3,
  spend_breakdown: Wallet,
  composite_stat: LayoutList,
});

/**
 * Renderer keys the backend spells differently from the drawing's name.
 *
 * Twenty of the catalog's keys hit `ICON_BY_RENDERER` outright; these eight map
 * onto the same drawing under another name — the same aliasing `@aimsos/ui`'s
 * catalog registry does.
 */
const RENDERER_ALIAS = Object.freeze({
  metric_row: 'stat_row',
  indicator: 'gauge',
  heatmap: 'heat_map',
  pie_chart: 'pie',
  area_chart: 'area',
  activity: 'feed',
  usage_map: 'usage_heatmap',
  composite_metric: 'composite_stat',
});

/**
 * @param {string} rendererKey The type's `rendererKey`, from the catalog.
 * @returns {Function|null} A `lucide-react` component, or `null` for a renderer
 *   this map has not seen — the tile renders without an icon rather than not
 *   at all, so a newly seeded type is never hidden by a missing drawing.
 */
export const widgetTypeIconByRenderer = (rendererKey) => (
  ICON_BY_RENDERER[RENDERER_ALIAS[rendererKey] ?? rendererKey] ?? null
);

export default widgetTypeIconByRenderer;
